import path from 'path';

import { AddressResponse, BaseReferenceResponse } from '~interfaces/responses';
import { TopologyService } from '~services';

import PaymentMethodStatusEnum from '../enums/paymentMethodStatusEnum';
import PaymentMethod from '../interfaces/paymentMethod';

class PaymentMethodsService extends TopologyService {
	protected readonly path = 'paymentmethods';

	public readonly personalSubPath = 'personal';
	public readonly adyenSubPath = 'adyen';

	constructor() {
		super('v1');
	}

	async getPaymentMethodsUnmapped(args: { userId: string }): Promise<object> {
		const { data } = await this._client.get(path.join(this.path), {
			params: {
				userId: args.userId,
			},
		});

		return data;
	}

	async getPersonalPaymentMethod(args: { userId: string }): Promise<PaymentMethod | null> {
		const { data } = await this._client.get<PersonalPaymentMethodResponse>(
			path.join(this.path, this.personalSubPath),
			{
				params: {
					userId: args.userId,
				},
			}
		);

		if (data?.status === 'unavailable') {
			return null;
		}

		return PaymentMethodsService.fromResponse(data);
	}

	async getAdyenSupportedPaymentMethods(args: {
		userId?: string;
		channel?: 'android' | 'web' | 'iOS';
		countryCode?: string;
	}): Promise<{ type: string }[]> {
		const { data } = await this._client.get<{ type: string }[]>(
			path.join(this.adyenSubPath, this.path),
			{
				params: args,
			}
		);

		return data;
	}

	async removePaymentMethod(userId: string, data): Promise<null> {
		await this._client.delete<null, null, PaymentMethodRemovalRequest>(
			path.join(this.path, userId),
			{
				data: data,
			}
		);
		return null;
	}

	static fromResponse(data: PersonalPaymentMethodResponse): PaymentMethod {
		const { id, name, logo, creationDate, status, ...rest } = data;

		return {
			...rest,
			...(id != null &&
				name != null &&
				PaymentMethodsService.fromBaseReferenceResponse({ id: id, name: name })),
			logoUri: logo,
			creationDate: creationDate ? new Date(creationDate) : undefined,
			status: status as PaymentMethodStatusEnum,
		};
	}
}

interface PersonalPaymentMethodResponse extends Partial<BaseReferenceResponse> {
	status: string;
	creationDate?: string;
	logo?: string;
	invoiceContact?: {
		name: string;
		address: AddressResponse;
	};
	bank?: {
		bankName: string;
		bic: string;
		countryCode: string;
		iban: string;
		ownerName: string;
	};
	card?: {
		expiryMonth: string;
		expiryYear: string;
		holderName: string;
		number: string;
	};
	paypal?: {
		email: string;
		payerId: string;
		payerStatus: string;
		phone: string;
		payerResidenceCountry: string;
	};
}

interface PaymentMethodRemovalRequest {
	message?: string;
}

export default PaymentMethodsService;
